import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import apiServices from '../../../../../services/apiService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft, faInfoCircle, faCheckCircle, faClock, faCircleDot,
    faFilePdf, faDownload, faPlus, faExclamationTriangle, faChevronDown,
    faTimes, faPen, faUpload, faQrcode, faBuildingColumns, faCreditCard,
    faMoneyCheck, faFileContract, faCertificate, faFileSignature
} from '@fortawesome/free-solid-svg-icons';
import PageHeadingSection from '../../../../common/pageheading/PageHeadingSection';
import Breadcrumbs from '../../../../common/BreadCrum/BreadCrum';
import { primaryColor } from '../../../../../utils/comon';
import CommonModal from '../../../../common/commonmodal/CommonModal';
import CustomSelect from '../../../../common/formfields/CustomSelect';
import { getStatusBadgeStyle } from '../../../../../utils/statusUtils';

const LeaseDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const { user } = useSelector((state) => state.auth);
    const roleId = Number(user?.roleId || 3); // Default Site Admin

    const [leaseData, setLeaseData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isApproving, setIsApproving] = useState(false);

    const fetchLease = async () => {
        const siteId = user?.siteId;
        const unitId = user?.unitId || user?.branchorunitID;

        let endpoint = '';
        if (roleId === 4 || roleId === 6) {
            if (!unitId) return;
            endpoint = `/RentalParking/unit/${unitId}`;
        } else {
            if (!siteId) return;
            endpoint = `/RentalParking/site/${siteId}`;
        }

        try {
            const res = await apiServices({
                method: 'GET',
                url: endpoint
            });

            if (res?.data?.isSuccess === true || res?.data?.IsSuccess === true) {
                const responseData = res?.Data || res?.data?.Data || [];
                const foundLease = responseData.find(item => item.Id.toString() === id);
                if (foundLease) {
                    setLeaseData(foundLease);
                } else {
                    toast.error("Lease not found");
                    navigate(-1);
                }
            }
        } catch (error) {
            console.error("Error fetching lease data", error);
            toast.error("Failed to fetch lease details");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchLease();
    }, [id, user, roleId, navigate]);

    const handleApprove = async () => {
        setIsApproving(true);
        try {
            const res = await apiServices({
                method: 'PATCH',
                url: `/RentalParking/${id}/approve`
            });
            if (res?.data?.isSuccess === true || res?.data?.IsSuccess === true) {
                toast.success("Lease approved successfully!");
                await fetchLease();
            } else {
                toast.error(res?.message || res?.data?.Message || "Failed to approve lease");
            }
        } catch (error) {
            console.error("Error approving lease", error);
            toast.error("An error occurred during approval");
        } finally {
            setIsApproving(false);
        }
    };

    const lease = leaseData ? {
        id: (leaseData.Id || leaseData.id) ? (leaseData.Id || leaseData.id).toString() : "-",
        slot: leaseData.SlotNumber || leaseData.slotNumber || "-",
        provider: leaseData.OwnerUnitName || leaseData.ownerUnitName || (leaseData.OwnerUnitId || leaseData.ownerUnitId ? `Unit #${leaseData.OwnerUnitId || leaseData.ownerUnitId}` : "Site / Management"),
        renter: leaseData.RenterUnitName || leaseData.renterUnitName || (leaseData.RenterUnitId || leaseData.renterUnitId ? `Unit #${leaseData.RenterUnitId || leaseData.renterUnitId}` : "— (Awaiting Allocation)"),
        status: leaseData.Status || leaseData.status || "-",
        allocationType: leaseData.AllocationTypeName || leaseData.allocationTypeName || "-",
        startDate: (leaseData.StartDate || leaseData.startDate) ? new Date(leaseData.StartDate || leaseData.startDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : "-",
        endDate: (leaseData.EndDate || leaseData.endDate) ? new Date(leaseData.EndDate || leaseData.endDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : "-",
        createdAt: (leaseData.CreatedOn || leaseData.createdOn) ? new Date(leaseData.CreatedOn || leaseData.createdOn).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : "-",
    } : null;

    // State Machine: 1=Created, 2=Pending Accept, 3=Pending Allocation, 4=Allocated, 5=Active
    const [leaseStep, setLeaseStep] = useState(2);

    // Update step when leaseData changes
    useEffect(() => {
        if (leaseData) {
            const status = leaseData.Status?.toLowerCase();
            if (status === "listed") setLeaseStep(1);
            else if (status === "pendingapproval" || status === "pending") setLeaseStep(2);
            else if (status === "approved") setLeaseStep(3);
            else if (status === "allocated") setLeaseStep(4);
            else if (status === "cancelled" || status === "rejected") setLeaseStep(-1);
            else setLeaseStep(5);
        }
    }, [leaseData]);

    const [isStatusChanging, setIsStatusChanging] = useState(false);
    const handleStatusChange = async (newStatus) => {
        setIsStatusChanging(true);
        try {
            const res = await apiServices({
                method: 'PATCH',
                url: `/RentalParking/${id}/status?status=${newStatus}`
            });
            if (res?.data?.isSuccess === true || res?.data?.IsSuccess === true) {
                toast.success(`Lease ${newStatus.toLowerCase()} successfully!`);
                await fetchLease();
            } else {
                toast.error(res?.message || res?.data?.Message || `Failed to change status to ${newStatus}`);
            }
        } catch (error) {
            console.error(`Error changing status to ${newStatus}`, error);
            toast.error("An error occurred");
        } finally {
            setIsStatusChanging(false);
        }
    };

    const [isReleasing, setIsReleasing] = useState(false);
    const handleRelease = async () => {
        setIsReleasing(true);
        try {
            const res = await apiServices({
                method: 'PATCH',
                url: `/RentalParking/${id}/complete`
            });
            if (res?.data?.isSuccess === true || res?.data?.IsSuccess === true) {
                toast.success("Lease released successfully!");
                await fetchLease();
            } else {
                toast.error(res?.message || res?.data?.Message || "Failed to release lease");
            }
        } catch (error) {
            console.error("Error releasing lease", error);
            toast.error("An error occurred");
        } finally {
            setIsReleasing(false);
        }
    };

    const [isAllocateModalOpen, setIsAllocateModalOpen] = useState(false);
    const [units, setUnits] = useState([]);
    const [allocationTypes, setAllocationTypes] = useState([]);
    const [unitSearch, setUnitSearch] = useState("");
    const [isAllocating, setIsAllocating] = useState(false);
    const [allocateData, setAllocateData] = useState({
        renterUnitId: "",
        allocationTypeId: ""
    });
    const [selectedUnit, setSelectedUnit] = useState(null);

    // Fetch Units and Allocation Types for Modal
    useEffect(() => {
        if (isAllocateModalOpen) {
            const fetchModalData = async () => {
                const siteId = user?.siteId;
                if (!siteId) return;
                try {
                    // Fetch Units
                    const unitRes = await apiServices({ method: "GET", url: `/UnitMaster/GetAllUnitMaster?siteId=${siteId}&pageNumber=1&pageSize=10000` });
                    
                    let allUnits = [];
                    if (Array.isArray(unitRes?.data?.data)) allUnits = unitRes.data.data;
                    else if (Array.isArray(unitRes?.data)) allUnits = unitRes.data;
                    else if (Array.isArray(unitRes?.Data)) allUnits = unitRes.Data;
                    else if (Array.isArray(unitRes)) allUnits = unitRes;

                    const filteredUnits = allUnits.filter(u => Number(u.SiteId) === Number(siteId));
                    setUnits(filteredUnits);
                    // Fetch Allocation Types
                    const typeRes = await apiServices({ method: "GET", url: `/AllocationTypeMaster/site/${siteId}` });
                    if (typeRes?.data?.isSuccess === true || typeRes?.data?.IsSuccess === true) {
                        setAllocationTypes(typeRes?.data?.Data || typeRes?.data?.data || []);
                    }
                } catch (err) {
                    console.error("Error fetching allocation modal data", err);
                }
            };
            fetchModalData();
        }
    }, [isAllocateModalOpen, user?.siteId]);

    const handleAllocate = async () => {
        if (!allocateData.renterUnitId || !allocateData.allocationTypeId) {
            toast.error("Please select both Unit and Allocation Type.");
            return;
        }
        setIsAllocating(true);
        try {
            const res = await apiServices({
                method: "PATCH",
                url: `/RentalParking/${id}/allocate`,
                data: {
                    renterUnitId: Number(allocateData.renterUnitId),
                    allocationTypeId: Number(allocateData.allocationTypeId)
                }
            });
            if (res?.data?.isSuccess === true || res?.data?.IsSuccess === true) {
                toast.success("Lease allocated successfully!");
                await fetchLease();
                setIsAllocateModalOpen(false);
            } else {
                toast.error(res?.message || res?.data?.Message || "Failed to allocate lease");
            }
        } catch (err) {
            console.error("Error allocating lease", err);
            toast.error("An error occurred during allocation");
        } finally {
            setIsAllocating(false);
        }
    };



    if (isLoading || !lease) {
        return (
            <div className="bg-dashboardbg px-4 sm:px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 justify-center items-center h-full">
                <p className="text-sm font-bold text-gray-500">Loading lease details...</p>
            </div>
        );
    }

    return (
        <div className="bg-dashboardbg px-4 sm:px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto h-full relative scrollbar-hide">
            <Breadcrumbs />
            <div className="flex items-center gap-3 mb-2 mt-2">
                <div>
                    <div className="flex items-center gap-3">
                        <h2 className="text-lg font-black text-gray-800 dark:text-textprimary">{lease.id}</h2>
                    </div>
                    <p className="text-[10px] text-gray-500 font-medium">Slot: {lease.slot} • Provider: {lease.provider} • Renter: {lease.renter}</p>
                </div>
            </div>

            <div className="mt-2 flex flex-col gap-4 w-full">
                {/* Unified Status Bar */}
                {(() => {
                    const status = leaseData?.Status?.toLowerCase() || "";
                    const isAdmin = roleId === 3;
                    
                    let message = `LEASE STATUS: ${leaseData?.Status?.toUpperCase()}`;
                    let buttons = null;
                    let icon = faInfoCircle;

                    if (status === "listed") {
                        message = "THIS LEASE IS CURRENTLY LISTED.";
                        if (isAdmin || roleId === 4) {
                            buttons = (
                                <button disabled={isStatusChanging} onClick={() => handleStatusChange("Cancelled")} className="px-5 py-2 rounded-lg text-[10px] font-bold text-gray-700 bg-white hover:bg-gray-50 transition-all shadow-sm border border-gray-300 disabled:opacity-70">
                                    {isStatusChanging ? "Cancelling..." : "Cancel Lease"}
                                </button>
                            );
                        }
                    } else if (leaseStep === 2) {
                        icon = faClock;
                        if (isAdmin) {
                            message = "ACTION REQUIRED: SITE ADMIN ACCEPTANCE IS REQUIRED FOR THIS LEASE OFFER.";
                            icon = faExclamationTriangle;
                            buttons = (
                                <div className="flex gap-2 w-full sm:w-auto">
                                    <button disabled={isStatusChanging || isApproving} onClick={() => handleStatusChange("Rejected")} className="flex-1 sm:flex-none px-5 py-2 rounded-lg text-[10px] font-bold text-gray-700 bg-white hover:bg-gray-50 transition-all shadow-sm border border-gray-300 disabled:opacity-70">
                                        {isStatusChanging ? "Rejecting..." : "Reject"}
                                    </button>
                                    <button disabled={isApproving || isStatusChanging} onClick={handleApprove} className="flex-1 sm:flex-none px-5 py-2 rounded-lg text-[10px] font-bold text-white bg-[#10B981] hover:brightness-95 transition-all shadow-sm disabled:opacity-70">
                                        {isApproving ? "Approving..." : "Accept Proposal"}
                                    </button>
                                </div>
                            );
                        } else {
                            message = "Awaiting Review & Acceptance by Site Admin";
                        }
                    } else if (leaseStep === 3) {
                        icon = faClock;
                        if (isAdmin) {
                            message = "ACTION REQUIRED: ALLOCATE LEASE TO A TENANT UNIT.";
                            icon = faExclamationTriangle;
                            buttons = (
                                <button onClick={() => setIsAllocateModalOpen(true)} className="px-5 py-2 rounded-lg text-[10px] font-bold text-white bg-indigo-600 hover:bg-indigo-700 transition-all shadow-sm">
                                    Allocate Lease
                                </button>
                            );
                        } else {
                            message = "Awaiting Lease Allocation by Site Admin";
                        }
                    } else if (status === "rented") {
                        message = "THIS LEASE IS CURRENTLY ACTIVE AND RENTED.";
                        icon = faCheckCircle;
                        if (isAdmin) {
                            buttons = (
                                <button disabled={isReleasing} onClick={handleRelease} className="px-5 py-2 rounded-lg text-[10px] font-bold text-white bg-blue-600 hover:bg-blue-700 transition-all shadow-sm disabled:opacity-70">
                                    {isReleasing ? "Releasing..." : "Release Lease"}
                                </button>
                            );
                        }
                    } else if (status === "completed") {
                        message = "THIS LEASE HAS BEEN COMPLETED.";
                        icon = faCheckCircle;
                    } else if (status === "cancelled") {
                        message = "THIS LEASE HAS BEEN CANCELLED.";
                        icon = faTimes;
                    } else if (status === "rejected") {
                        message = "THIS LEASE OFFER WAS REJECTED.";
                        icon = faTimes;
                    }

                    const badgeClasses = getStatusBadgeStyle(status || "");
                    
                    return (
                        <div className={`border rounded-xl p-3 flex flex-col sm:flex-row items-center justify-between gap-3 shadow-sm ${badgeClasses} !bg-opacity-40`}>
                            <div className="flex items-center gap-2">
                                <FontAwesomeIcon icon={icon} className="text-current text-sm opacity-80" />
                                <span className="font-bold text-current text-[10px] tracking-wide uppercase">{message}</span>
                            </div>
                            {buttons && (
                                <div className="flex items-center gap-2 w-full sm:w-auto">
                                    {buttons}
                                </div>
                            )}
                        </div>
                    );
                })()}

                {/* Lifecycle Timeline */}
                <div className="bg-white dark:bg-cardbg border border-gray-200 dark:border-bordercolor rounded-xl p-4 shadow-sm">
                    <div className="flex justify-between items-center mb-3">
                        <div className="flex items-center gap-2 text-blue-600 font-bold text-[11px] uppercase tracking-wider">
                            <FontAwesomeIcon icon={faInfoCircle} /> LEASE EXECUTION LIFECYCLE
                        </div>
                        <span className="text-[10px] text-gray-400">Created: {lease.createdAt}</span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        {/* Step 1 */}
                        <div className={leaseStep === -1 ? "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/30 rounded-lg p-2.5 opacity-70" : "bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700/30 rounded-lg p-2.5 relative overflow-hidden"}>
                            <div className="flex items-start gap-2">
                                <FontAwesomeIcon icon={leaseStep === -1 ? faTimes : faCheckCircle} className={leaseStep === -1 ? "text-red-600 dark:text-red-500 mt-0.5 text-[11px]" : "text-green-600 dark:text-green-500 mt-0.5 text-[11px]"} />
                                <div>
                                    <h4 className={leaseStep === -1 ? "text-red-800 dark:text-red-400 font-bold text-[11px]" : "text-green-800 dark:text-green-400 font-bold text-[11px]"}>1. Proposal Created</h4>
                                    <p className={leaseStep === -1 ? "text-red-600 dark:text-red-500 text-[9px] mt-0.5" : "text-green-600 dark:text-green-500 text-[9px] mt-0.5"}>Submitted by {lease.provider}</p>
                                </div>
                            </div>
                        </div>

                        {/* Step 2 */}
                        <div className={leaseStep === -1 ? "bg-gray-50 dark:bg-actionitembg border border-gray-200 dark:border-bordercolor rounded-lg p-2.5 opacity-70" : leaseStep === 2 ? "bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700/30 rounded-lg p-2.5 shadow-sm" : "bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700/30 rounded-lg p-2.5"}>
                            <div className="flex items-start gap-2">
                                <FontAwesomeIcon icon={leaseStep === -1 ? faCircleDot : leaseStep === 2 ? faClock : faCheckCircle} className={leaseStep === -1 ? "text-gray-400 dark:text-gray-500 mt-0.5 text-[11px]" : leaseStep === 2 ? "text-yellow-600 dark:text-yellow-500 mt-0.5 text-[11px]" : "text-green-600 dark:text-green-500 mt-0.5 text-[11px]"} />
                                <div>
                                    <h4 className={leaseStep === -1 ? "text-gray-500 dark:text-gray-400 font-bold text-[11px]" : leaseStep === 2 ? "text-yellow-800 dark:text-yellow-400 font-bold text-[11px]" : "text-green-800 dark:text-green-400 font-bold text-[11px]"}>2. Site Acceptance</h4>
                                    <p className={leaseStep === -1 ? "text-gray-400 dark:text-gray-500 text-[9px] mt-0.5" : leaseStep === 2 ? "text-yellow-700 dark:text-yellow-500 text-[9px] mt-0.5" : "text-green-600 dark:text-green-500 text-[9px] mt-0.5"}>{leaseStep === -1 ? "Terminated" : leaseStep === 2 ? "Awaiting Review" : "Accepted by Site"}</p>
                                </div>
                            </div>
                        </div>

                        {/* Step 3 */}
                        <div className={leaseStep < 3 ? "bg-gray-50 dark:bg-actionitembg border border-gray-200 dark:border-bordercolor rounded-lg p-2.5 opacity-70" : leaseStep === 3 ? "bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700/30 rounded-lg p-2.5 shadow-sm" : "bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700/30 rounded-lg p-2.5"}>
                            <div className="flex items-start gap-2">
                                <FontAwesomeIcon icon={leaseStep < 3 ? faCircleDot : leaseStep === 3 ? faCircleDot : faCheckCircle} className={leaseStep < 3 ? "text-gray-400 dark:text-gray-500 mt-0.5 text-[11px]" : leaseStep === 3 ? "text-yellow-600 dark:text-yellow-500 mt-0.5 text-[11px]" : "text-green-600 dark:text-green-500 mt-0.5 text-[11px]"} />
                                <div>
                                    <h4 className={leaseStep < 3 ? "text-gray-500 dark:text-gray-400 font-bold text-[11px]" : leaseStep === 3 ? "text-yellow-800 dark:text-yellow-400 font-bold text-[11px]" : "text-green-800 dark:text-green-400 font-bold text-[11px]"}>3. Contract Execution</h4>
                                    <p className={leaseStep < 3 ? "text-gray-400 dark:text-gray-500 text-[9px] mt-0.5" : leaseStep === 3 ? "text-yellow-700 dark:text-yellow-500 text-[9px] mt-0.5" : "text-green-600 dark:text-green-500 text-[9px] mt-0.5"}>{leaseStep < 3 ? "Pending Signature" : leaseStep === 3 ? "Pending Signature" : "Contract Signed"}</p>
                                </div>
                            </div>
                        </div>

                        {/* Step 4 */}
                        <div className={leaseStep < 4 ? "bg-gray-50 dark:bg-actionitembg border border-gray-200 dark:border-bordercolor rounded-lg p-2.5 opacity-70" : leaseStep === 4 ? "bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700/30 rounded-lg p-2.5 shadow-sm" : "bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700/30 rounded-lg p-2.5"}>
                            <div className="flex items-start gap-2">
                                <FontAwesomeIcon icon={leaseStep < 4 ? faCircleDot : leaseStep === 4 ? faCircleDot : faCheckCircle} className={leaseStep < 4 ? "text-gray-400 dark:text-gray-500 mt-0.5 text-[11px]" : leaseStep === 4 ? "text-yellow-600 dark:text-yellow-500 mt-0.5 text-[11px]" : "text-green-600 dark:text-green-500 mt-0.5 text-[11px]"} />
                                <div>
                                    <h4 className={leaseStep < 4 ? "text-gray-500 dark:text-gray-400 font-bold text-[11px]" : leaseStep === 4 ? "text-yellow-800 dark:text-yellow-400 font-bold text-[11px]" : "text-green-800 dark:text-green-400 font-bold text-[11px]"}>4. Payment & Activation</h4>
                                    <p className={leaseStep < 4 ? "text-gray-400 dark:text-gray-500 text-[9px] mt-0.5" : leaseStep === 4 ? "text-yellow-700 dark:text-yellow-500 text-[9px] mt-0.5" : "text-green-600 dark:text-green-500 text-[9px] mt-0.5"}>{leaseStep < 4 ? "Pending Payment" : leaseStep === 4 ? "Pending Payment" : "Payment Received"}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-1 gap-4 mt-4">
                    {/* Lease Details Card */}
                    <div className="bg-white dark:bg-cardbg border border-gray-200 dark:border-bordercolor rounded-xl p-4 shadow-sm">
                        <div className="mb-4">
                            <h3 className="font-bold text-gray-800 dark:text-textprimary text-xs">Lease Information</h3>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-y-4 gap-x-3">
                            <div>
                                <h5 className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Slot Number</h5>
                                <p className="font-bold text-gray-800 text-xs">{lease.slot}</p>
                            </div>
                            <div>
                                <h5 className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Provider Unit</h5>
                                <p className="font-bold text-gray-800 text-xs">{lease.provider}</p>
                            </div>
                            <div>
                                <h5 className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Renter Unit</h5>
                                <p className="font-bold text-gray-800 text-xs">{lease.renter}</p>
                            </div>
                            <div>
                                <h5 className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Allocation Type</h5>
                                <p className="font-bold text-gray-800 text-xs">{lease.allocationType}</p>
                            </div>
                            <div>
                                <h5 className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Start Date</h5>
                                <p className="font-bold text-gray-800 text-xs">{lease.startDate}</p>
                            </div>
                            <div>
                                <h5 className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">End Date</h5>
                                <p className="font-bold text-gray-800 text-xs">{lease.endDate}</p>
                            </div>
                            <div>
                                <h5 className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Created On</h5>
                                <p className="font-bold text-gray-800 text-xs">{lease.createdAt}</p>
                            </div>
                            <div>
                                <h5 className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Status</h5>
                                <div className="mt-1">
                                    <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${getStatusBadgeStyle(lease.status)}`}>
                                        {lease.status === 'Active' ? 'Active' : lease.status}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-bordercolor flex justify-start">
                <button
                    onClick={() => navigate(-1)}
                    className="px-5 py-2 rounded-lg text-[11px] font-bold text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors shadow-sm bg-white dark:bg-cardbg"
                >
                    &lt; Cancel
                </button>
            </div>

            {/* Allocate Modal */}
            <CommonModal
                isOpen={isAllocateModalOpen}
                onClose={() => setIsAllocateModalOpen(false)}
                title="Allocate Lease"
                subTitle="Select the tenant unit and allocation type."
                footerContent={
                    <div className="flex justify-end gap-3 w-full">
                        <button onClick={() => setIsAllocateModalOpen(false)} className="px-5 py-2 rounded-lg text-[11px] font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">Cancel</button>
                        <button 
                            onClick={handleAllocate}
                            disabled={isAllocating}
                            className="px-5 py-2 rounded-lg text-[11px] font-bold text-white bg-[#10B981] hover:brightness-95 transition-all shadow-sm border border-black/10 disabled:opacity-70"
                        >
                            {isAllocating ? "Allocating..." : "Confirm Allocation"}
                        </button>
                    </div>
                }
            >
                <div className="flex flex-col gap-4">
                    <div>
                        <CustomSelect
                            label="Select Tenant Unit"
                            options={units.map(u => ({ label: u.UnitName, value: u.Id || u.UnitId }))}
                            value={selectedUnit}
                            onChange={(val) => {
                                setSelectedUnit(val);
                                setAllocateData(prev => ({ ...prev, renterUnitId: val ? val.value : "" }));
                            }}
                            placeholder="Search unit..."
                            required={true}
                        />
                    </div>
                    <div>
                        <label className="block text-[11px] font-bold text-gray-700 dark:text-gray-300 mb-1">Allocation Type <span className="text-red-500">*</span></label>
                        <select
                            value={allocateData.allocationTypeId}
                            onChange={(e) => setAllocateData(prev => ({ ...prev, allocationTypeId: e.target.value }))}
                            className="w-full border border-gray-200 dark:border-bordercolor bg-white dark:bg-cardbg rounded px-3 py-2 text-xs text-gray-600 dark:text-white outline-none focus:border-blue-500"
                        >
                            <option value="">Select Type</option>
                            {allocationTypes.map(t => (
                                <option key={t.Id} value={t.Id}>{t.AllocationTypeName}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </CommonModal>
        </div>
    );
};

export default LeaseDetails;
